import { db, auth } from '../config/firebase';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  doc,
  writeBatch,
  getDoc
} from 'firebase/firestore';
import NotificationService from './NotificationService';

class OrderService {
  static async placeOrder(orderData) {
    try {
      if (!auth.currentUser) throw new Error('User not authenticated');

      // Debug log the incoming data
      console.log('Raw order data:', JSON.stringify(orderData, null, 2));

      if (!orderData || !orderData.items || !Array.isArray(orderData.items)) {
        throw new Error('Invalid order data structure');
      }

      // Validate and clean items
      const validatedItems = (await Promise.all(orderData.items
        .map(async item => {
          console.log('Processing item:', JSON.stringify(item, null, 2));

          // Get product details to ensure we have the image
          const productDoc = await getDoc(doc(db, 'products', item.productId));
          const productData = productDoc.data();

          // Create clean item object
          return {
            productId: item.productId,
            quantity: Number(item.quantity) || 1,
            price: Number(item.price) || 0,
            product: {
              id: item.productId,
              title: item.product.title,
              price: Number(item.product.price) || 0,
              sellerId: item.product.sellerId,
              image: productData?.images?.[0] || item.product.image || null
            }
          };
        })))
        .filter(Boolean);

      if (validatedItems.length === 0) {
        throw new Error('No valid items in order');
      }

      // Validate shipping data
      const shipping = {
        fullName: String(orderData.shipping?.fullName || '').trim(),
        address: String(orderData.shipping?.address || '').trim(),
        city: String(orderData.shipping?.city || '').trim(),
        state: String(orderData.shipping?.state || '').trim(),
        zipCode: String(orderData.shipping?.zipCode || '').trim(),
        phone: String(orderData.shipping?.phone || '').trim()
      };

      // Create clean order document
      const orderDoc = {
        userId: auth.currentUser.uid,
        items: validatedItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.product.price,
          product: {
            id: item.product.id,
            title: item.product.title,
            price: item.product.price,
            sellerId: item.product.sellerId,
            image: item.product.image
          }
        })),
        status: 'pending',
        paymentMethod: String(orderData.paymentMethod || 'cash'),
        shipping: shipping,
        total: Number(orderData.total) || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Debug log the final document
      console.log('Final order document:', JSON.stringify(orderDoc, null, 2));

      // Create batch operations
      const batch = writeBatch(db);
      const orderRef = doc(collection(db, 'orders'));

      // Set order document
      batch.set(orderRef, orderDoc);

      // Clear cart
      const cartRef = doc(db, 'carts', auth.currentUser.uid);
      batch.update(cartRef, {
        items: [],
        updatedAt: serverTimestamp()
      });

      // Commit batch
      await batch.commit();

      // Get user data for notification
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      console.log('User document:', userDoc.data()); // Debug log

      const userData = userDoc.data();
      // Use shipping name if available, fallback to other sources
      const userName = orderData.shipping?.fullName || userData?.name || userData?.displayName || auth.currentUser.displayName || 'Anonymous';
      console.log('Selected username:', userName); // Debug log

      // Create notifications
      for (const item of validatedItems) {
        const { sellerId } = item.product;
        
        await NotificationService.createOrderNotification({
          receiverId: sellerId,
          senderId: auth.currentUser.uid,
          senderName: userName, // Use shipping name
          senderPhoto: userData?.photoURL || auth.currentUser.photoURL,
          orderId: orderRef.id,
          productId: item.productId,
          productTitle: item.product.title,
          quantity: item.quantity,
          total: item.price * item.quantity
        });
      }

      return orderRef.id;
    } catch (error) {
      console.error('Error placing order:', error);
      throw new Error(`Failed to place order: ${error.message}`);
    }
  }

  static async getUserOrders(userId) {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user orders:', error);
      throw error;
    }
  }

  static async getReceivedOrders(userId) {
    try {
      // First, get all products of this seller
      const productsRef = collection(db, 'products');
      const productsQuery = query(productsRef, where('sellerId', '==', userId));
      const productsSnapshot = await getDocs(productsQuery);
      
      // Get the IDs of all products owned by this seller
      const sellerProductIds = productsSnapshot.docs.map(doc => doc.id);

      // Get all orders
      const ordersRef = collection(db, 'orders');
      const ordersSnapshot = await getDocs(ordersRef);
      
      // Filter orders that contain any of the seller's products
      const receivedOrders = ordersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(order => 
          // Check if any item in the order contains a product ID that belongs to the seller
          order.items?.some(item => 
            sellerProductIds.includes(item.productId) || 
            sellerProductIds.includes(item.product?.id)
          )
        )
        .sort((a, b) => 
          (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );

      return receivedOrders;
    } catch (error) {
      console.error('Error getting received orders:', error);
      throw error;
    }
  }
}

export default OrderService; 